import { DialogTrigger } from 'react-aria-components';

import { Button, Popup, Stack, Text, toast } from '@ekhodealer/ekho-common/rev';

import { useUpdateDiscount } from '../api/useUpdateDiscount';

interface DeactivateDiscountDialogProps {
	discountId: string;
	discountName: string;
	/** The discount's scheduled start time (Unix ms) — used to detect scheduled discounts */
	startsAt: number;
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
}

/**
 * Confirmation dialog for deactivating a discount.
 * Deactivation sets the discount's endsAt to the current time, making it "Expired".
 */
export const DeactivateDiscountDialog = ({
	discountId,
	discountName,
	startsAt,
	isOpen,
	onOpenChange,
}: DeactivateDiscountDialogProps) => {
	const { mutate: updateDiscount, isPending } = useUpdateDiscount();

	const handleDeactivate = () => {
		const now = Date.now();
		const isScheduled = startsAt > now;

		updateDiscount(
			{
				discountId,
				// When deactivating a scheduled discount, set both startsAt and endsAt to now
				// so that endsAt is never before startsAt (which would be an invalid date range)
				payload: isScheduled ? { startsAt: now, endsAt: now } : { endsAt: now },
			},
			{
				onSuccess: () => onOpenChange(false),
				onError: () => {
					toast({
						message: 'Failed to deactivate discount. Please try again.',
						variant: 'error',
					});
				},
			},
		);
	};

	return (
		<DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
			<Popup title='Deactivate discount?'>
				<Stack padding='0' itemsSpacing='24'>
					<Text size='body' color='secondary'>
						Deactivating <strong>{discountName}</strong> will stop it from applying
						to new purchases. You can reactivate it at any time.
					</Text>
					<div
						style={{
							display: 'flex',
							justifyContent: 'flex-end',
							gap: '12px',
							paddingTop: '8px',
						}}>
						<Button
							variant='secondary'
							size='medium'
							onPress={() => onOpenChange(false)}
							isDisabled={isPending}>
							Cancel
						</Button>
						<Button
							variant='filled'
							size='medium'
							onPress={handleDeactivate}
							isLoading={isPending}>
							Deactivate
						</Button>
					</div>
				</Stack>
			</Popup>
		</DialogTrigger>
	);
};
